import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, MapPin } from "lucide-react";
import PageWrapper from "../components/PageWrapper";
import { FadeUp } from "../components/motion";

const PROJECTS = [
  {
    img: "/images/gen/project-hill-resort.jpg",
    name: "Aravalli Ridge Retreat",
    location: "Kasauli, Himachal Pradesh",
    type: "Hospitality & Resort",
    stats: [["12", "A-Frame keys"], ["94", "Days to open"], ["38%", "Projected ROI"]],
    blurb: "A terraced hillside retreat of A-frame cabins — zero-cut foundations, revenue in one season.",
  },
  {
    img: "/images/gen/project-beach-villa.jpg",
    name: "Palm & Tide Villa",
    location: "Ashwem, Goa",
    type: "Precast Villas",
    stats: [["2,200", "Sq ft villa"], ["120", "Days delivered"], ["4.9★", "Guest rating"]],
    blurb: "A precast beach villa with infinity pool — engineered for salt air, monsoon and full-house buyouts.",
  },
  {
    img: "/images/gen/project-glamping.jpg",
    name: "Stargazer Glamp Village",
    location: "Jawai, Rajasthan",
    type: "Space Capsules",
    stats: [["8", "Capsule suites"], ["60", "Days installed"], ["₹18K", "ADR achieved"]],
    blurb: "Eight Stargazer capsules under desert skies — the highest-ADR inventory in the region.",
  },
  {
    img: "/images/gen/project-container-cafe.jpg",
    name: "The Foundry Café",
    location: "Hyderabad, Telangana",
    type: "Commercial Pods",
    stats: [["640", "Sq ft module"], ["38", "Days to launch"], ["2×", "Footfall vs. target"]],
    blurb: "A corten container café dropped into an urban plaza — brewing on day 39.",
  },
  {
    img: "/images/gen/project-farmhouse.jpg",
    name: "Mirage Farm Estate",
    location: "Manesar, Haryana",
    type: "Prefab Homes",
    stats: [["1,800", "Sq ft home"], ["82", "Days handover"], ["100%", "Factory finished"]],
    blurb: "A weekend farmhouse with vaulted living — delivered complete while the lawn was still being laid.",
  },
  {
    img: "/images/gen/project-wellness.jpg",
    name: "Stillwater Wellness Retreat",
    location: "Rishikesh, Uttarakhand",
    type: "Hospitality & Resort",
    stats: [["6", "Pavilions + suites"], ["110", "Days to open"], ["9.2", "Booking score"]],
    blurb: "Lakeside pavilions and float suites for a wellness brand — serenity, factory-built.",
  },
];

export default function Projects() {
  return (
    <PageWrapper>
      {/* Hero */}
      <section className="relative pt-[76px]" data-testid="projects-hero">
        <div className="relative h-[380px] md:h-[480px] overflow-hidden">
          <img src="/images/gen/resort-aerial.jpg" alt="NUVUN delivered projects" className="absolute inset-0 w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#0A0908] via-[#0A0908]/55 to-[#0A0908]/25" />
          <div className="absolute inset-0 flex items-end">
            <div className="max-w-[1440px] mx-auto px-5 md:px-10 lg:px-14 pb-10 w-full">
              <div className="eyebrow eyebrow-dash text-[#D9A441] mb-4">Projects</div>
              <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl text-[#F2EDE3]">
                Delivered. <span className="italic text-[#F2EDE3]/70">Not just designed.</span>
              </h1>
              <p className="mt-4 text-sm md:text-base text-[#F2EDE3]/60 max-w-xl">
                From Himalayan ridgelines to Goan shores — NUVUN structures are earning
                revenue and memories across India.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Stats strip */}
      <section className="border-b border-[#D9A441]/15" data-testid="projects-stats">
        <div className="max-w-[1440px] mx-auto px-5 md:px-10 lg:px-14 grid grid-cols-2 md:grid-cols-4 divide-x divide-[#D9A441]/12">
          {[["600+", "Units delivered"], ["50+", "Cities & destinations"], ["45–90", "Day average delivery"], ["20 yr", "Structural warranty"]].map(([v, l]) => (
            <div key={l} className="py-8 text-center">
              <div className="font-condensed text-3xl md:text-4xl text-[#D9A441]">{v}</div>
              <div className="eyebrow !text-[9px] text-[#F2EDE3]/50 mt-2">{l}</div>
            </div>
          ))}
        </div>
      </section>

      {/* Gallery */}
      <section className="py-16 lg:py-24" data-testid="projects-gallery">
        <div className="max-w-[1440px] mx-auto px-5 md:px-10 lg:px-14 space-y-16 lg:space-y-24">
          {PROJECTS.map((p, i) => (
            <FadeUp key={p.name}>
              <div className={`grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-14 items-center ${i % 2 ? "lg:[&>*:first-child]:order-2" : ""}`}>
                <div className="lg:col-span-7">
                  <div className="group relative overflow-hidden rounded-lg gold-frame">
                    <img src={p.img} alt={p.name} loading="lazy" className="product-card-img w-full h-[300px] md:h-[440px] object-cover" />
                    <span className="absolute top-5 left-5 eyebrow !text-[9px] bg-[#0A0908]/70 backdrop-blur-sm border border-[#D9A441]/40 text-[#D9A441] px-3 py-1.5 rounded-sm">
                      {p.type}
                    </span>
                  </div>
                </div>
                <div className="lg:col-span-5">
                  <div className="font-condensed text-[#D9A441]/70 text-lg">{String(i + 1).padStart(2, "0")}</div>
                  <h2 className="font-display text-3xl md:text-4xl text-[#F2EDE3] mt-2">{p.name}</h2>
                  <div className="flex items-center gap-2 mt-3 text-xs text-[#F2EDE3]/55">
                    <MapPin className="w-3.5 h-3.5 text-[#D9A441]" /> {p.location}
                  </div>
                  <p className="mt-5 text-sm text-[#F2EDE3]/60 leading-relaxed max-w-md">{p.blurb}</p>
                  <div className="mt-7 grid grid-cols-3 gap-3 max-w-md">
                    {p.stats.map(([v, l]) => (
                      <div key={l} className="panel px-3 py-4 text-center">
                        <div className="font-condensed text-xl text-[#D9A441]">{v}</div>
                        <div className="text-[9px] uppercase tracking-[0.14em] text-[#F2EDE3]/50 mt-1.5">{l}</div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </FadeUp>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="border-t border-[#D9A441]/15" data-testid="projects-cta">
        <div className="max-w-[1440px] mx-auto px-5 md:px-10 lg:px-14 py-14 flex flex-col md:flex-row items-start md:items-center justify-between gap-8">
          <FadeUp>
            <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl text-[#F2EDE3]">
              Your site could be <span className="italic text-[#D9A441]">next.</span>
            </h2>
          </FadeUp>
          <FadeUp delay={0.1}>
            <Link to="/contact" data-testid="projects-start-cta" className="btn-gold whitespace-nowrap">
              Start Your Project <ArrowRight className="w-4 h-4" />
            </Link>
          </FadeUp>
        </div>
      </section>
    </PageWrapper>
  );
}
